import {
  CAMERA_3D_FAR,
  CAMERA_3D_FOV,
  CAMERA_3D_NEAR,
  CONTROLS_3D_MIN_DISTANCE,
  CONTROLS_3D_SMOOTH,
  CONTROLS_DOLLY_SPEED,
  MODE_3D_VIEW,
} from '@constants'
import { useCachedCamera3D } from '@hooks/useCachedCamera3D'
import { useCameraControls3D } from '@hooks/useCameraControls3D'
import { useMouseChordPanCamera } from '@hooks/useMouseChordPanCamera'
import { CameraControls, PerspectiveCamera } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { useAppStore, useUiPresentationStore } from '@store'
import { AnyID } from '@types'
import consoleOnce from '@utils/consoleOnce'
import { startTransition, useRef, useEffect } from 'react'
import isArea from '@utils/sceneObjects/assertions/isArea'
import { applyDrawCameraFit } from '@utils/camera/applyDrawCameraFit'
import { registerCameraCaptureContext } from '@utils/camera/cameraControlsRegistry'
import {
  applyCameraViewSafely,
  clampCameraElevationViaControls,
  CAMERA_PRESERVATION_DRIFT_EPSILON,
  isCameraNavigationLocked,
  resolveCameraNavigationMode,
  shouldBlockProgrammaticCameraView,
} from '@utils/camera/cameraNavigationController'
import {
  applyCameraViewAfterFrames,
  captureCameraViewFromControls,
  hasCameraViewDrift,
  isValidCameraView,
} from '@utils/camera/cameraView'
import { Vector3, type PerspectiveCamera as ThreePerspectiveCamera } from 'three'

export function CameraManager3D() {
  const isModalOpen = useAppStore.use.isModalOpen()
  const activeSheet = useUiPresentationStore((s) => s.activeSheet)
  const mode = useAppStore.use.mode()
  const lastView = useAppStore((store) => store.lastView)
  const idDisenos = useAppStore((store) => store.clientInfo.idDisenos)
  const idVersiones = useAppStore((store) => store.clientInfo.idVersiones)
  const { controlsRef } = useCameraControls3D()
  useCachedCamera3D()
  const editCameraBoundingBox = useAppStore.use.editCameraBoundingBox()
  const cameraObjectRef = useRef(null)
  const cameraOBB = useAppStore.use.cameraOBB()
  const sceneBoundingBoxes = useAppStore.use.sceneBoundingBoxes()
  const editWall = useAppStore.use.editWallProperty()
  const editAreaProperty = useAppStore.use.editAreaProperty()
  const items = useAppStore.use.items()
  const areas = useAppStore.use.areas()
  const VERTECHO = useAppStore.use.prefs()?.VERTECHO
  const { raycaster, camera, size, viewport, gl, scene } = useThree()
  const shouldFitCameraToItems = useAppStore.use.shouldFitCameraToItems()
  const clearCameraFitToItems = useAppStore.use.clearCameraFitToItems()
  const cameraViewPreservationDepth = useAppStore.use.cameraViewPreservationDepth()
  const lockedCameraView = useAppStore((s) => s.lockedCameraView)
  const cameraViewRestoreNonce = useAppStore((s) => s.cameraViewRestoreNonce)
  const setLastView = useAppStore.use.setLastView()
  const lastCameraLogRef = useRef<{ position: Vector3; target: Vector3; timestamp: number } | null>(
    null,
  )
  const canPersistCameraViewRef = useRef(false)
  const restoredProjectKeyRef = useRef<string | null>(null)
  const projectKey = `${idDisenos}-${idVersiones}`

  const itemDragActive = useModularUiStore((s) => s.itemDragActive)
  const isDraggingItem =
    mode === 'MODE_DRAGGING_ITEM_3D' || mode === 'MODE_DRAGGING_ITEM' || itemDragActive
  // Sheet abierto (touch): bloquear cámara para que el scroll del panel no pelee con Orbit.
  const navigationMode = resolveCameraNavigationMode({
    isModalOpen: isModalOpen || Boolean(activeSheet),
    isDraggingItem,
    itemDragActive,
    cameraViewPreservationDepth,
    shouldFitCameraToItems,
  })
  const cameraControlsEnabled = !isCameraNavigationLocked(navigationMode)

  useMouseChordPanCamera(controlsRef, { enabled: cameraControlsEnabled })

  useEffect(() => {
    canPersistCameraViewRef.current = false
    restoredProjectKeyRef.current = null
  }, [projectKey])

  useEffect(() => {
    const controls = controlsRef.current
    if (
      !controls ||
      !camera ||
      !('isPerspectiveCamera' in camera) ||
      !camera.isPerspectiveCamera ||
      !gl ||
      !scene
    ) {
      registerCameraCaptureContext(null)
      return
    }

    registerCameraCaptureContext({
      controls,
      camera: camera as ThreePerspectiveCamera,
      gl,
      scene,
    })
    return () => registerCameraCaptureContext(null)
  }, [controlsRef, camera, gl, scene])

  useEffect(() => {
    if (
      mode !== MODE_3D_VIEW ||
      !controlsRef.current ||
      shouldFitCameraToItems ||
      isDraggingItem ||
      cameraViewPreservationDepth > 0
    ) {
      return
    }

    if (!lastView || !isValidCameraView(lastView)) {
      canPersistCameraViewRef.current = true
      return
    }

    if (restoredProjectKeyRef.current === projectKey) {
      canPersistCameraViewRef.current = true
      return
    }

    const controls = controlsRef.current
    if (!controls) return

    applyCameraViewSafely(controls, lastView, { animate: false, reason: 'project-restore' })
    restoredProjectKeyRef.current = projectKey
    canPersistCameraViewRef.current = true
  }, [mode, lastView, projectKey, controlsRef, shouldFitCameraToItems, isDraggingItem, cameraViewPreservationDepth])

  useEffect(() => {
    if (!controlsRef.current || !camera || shouldFitCameraToItems || isDraggingItem) return
    if (cameraViewPreservationDepth > 0) return

    const persistCameraView = () => {
      if (!canPersistCameraViewRef.current) return

      const controls = controlsRef.current
      if (!controls) return

      const capturedView = captureCameraViewFromControls(controls, camera)
      const currentPosition = capturedView.position
      const currentTarget = capturedView.target
      const now = Date.now()

      // Solo loguear si ha pasado al menos 500ms desde el último log o si la posición cambió significativamente
      const lastLog = lastCameraLogRef.current
      if (lastLog) {
        const timeSinceLastLog = now - lastLog.timestamp
        const positionChanged =
          currentPosition.distanceTo(lastLog.position) > 10 ||
          currentTarget.distanceTo(lastLog.target) > 10

        if (timeSinceLastLog < 500 && !positionChanged) {
          return
        }
      }

      lastCameraLogRef.current = {
        position: currentPosition,
        target: currentTarget,
        timestamp: now,
      }

      setLastView({
        position: currentPosition,
        target: currentTarget,
      })
    }

    const controls = controlsRef.current
    const handleControlEnd = () => {
      persistCameraView()
    }

    controls?.addEventListener('controlend', handleControlEnd)

    return () => {
      controls?.removeEventListener('controlend', handleControlEnd)
    }
  }, [controlsRef, camera, shouldFitCameraToItems, isDraggingItem, setLastView, cameraViewPreservationDepth])

  useEffect(() => {
    if (
      !controlsRef.current ||
      !camera ||
      cameraViewRestoreNonce === 0 ||
      shouldBlockProgrammaticCameraView({ isDraggingItem })
    ) {
      return
    }

    const controls = controlsRef.current
    if (!controls || !lastView || !isValidCameraView(lastView)) return

    const view = {
      position: lastView.position.clone(),
      target: lastView.target.clone(),
    }

    return applyCameraViewAfterFrames(controls, view, 1)
  }, [cameraViewRestoreNonce, controlsRef, camera, lastView, isDraggingItem])

  // Ajustar la cámara para enfocar unidades solo cuando se solicita explícitamente
  // (no reaccionar a cada cambio de `items`, p. ej. sustitución de unidad).
  useEffect(() => {
    if (
      !shouldFitCameraToItems ||
      !controlsRef.current ||
      isModalOpen ||
      isDraggingItem ||
      cameraViewPreservationDepth > 0
    ) {
      return
    }

    const controls = controlsRef.current
    if (!controls || !camera) return

    const fitFrame = window.requestAnimationFrame(() => {
      const liveState = useAppStore.getState()
      if (
        liveState.unitReplaceInFlight ||
        liveState.cameraViewPreservationDepth > 0 ||
        liveState.mode === 'MODE_DRAGGING_ITEM_3D' ||
        liveState.mode === 'MODE_DRAGGING_ITEM' ||
        useModularUiStore.getState().itemDragActive
      ) {
        clearCameraFitToItems()
        return
      }

      const fittedView = applyDrawCameraFit(controls, camera, liveState.items)
      if (!fittedView) {
        clearCameraFitToItems()
        return
      }

      setLastView({
        position: fittedView.position.clone(),
        target: fittedView.target.clone(),
      })
      clearCameraFitToItems()
    })

    return () => {
      window.cancelAnimationFrame(fitFrame)
    }
  }, [
    shouldFitCameraToItems,
    controlsRef,
    camera,
    isModalOpen,
    isDraggingItem,
    clearCameraFitToItems,
    cameraViewPreservationDepth,
    setLastView,
  ])

  useFrame(() => {
    if (
      shouldBlockProgrammaticCameraView({ isDraggingItem }) ||
      cameraViewPreservationDepth <= 0 ||
      !lockedCameraView ||
      !controlsRef.current ||
      !camera
    ) {
      return
    }

    const controls = controlsRef.current
    const currentView = captureCameraViewFromControls(controls, camera)
    if (
      hasCameraViewDrift(currentView, lockedCameraView, CAMERA_PRESERVATION_DRIFT_EPSILON)
    ) {
      applyCameraViewSafely(controls, lockedCameraView, {
        animate: false,
        reason: 'preservation',
      })
    }
  })

  const wallOcclusionFrameRef = useRef(0)
  const wallVisibleCacheRef = useRef<Map<string, boolean>>(new Map())
  const ceilingVisibleCacheRef = useRef<Map<string, boolean>>(new Map())
  const wallYResetDoneRef = useRef<Set<string>>(new Set())

  useFrame(() => {
    if (
      !cameraObjectRef.current ||
      !cameraOBB ||
      !raycaster ||
      !raycaster.camera ||
      !raycaster.camera.position ||
      !raycaster.camera.rotation ||
      location.href.includes('openProject') ||
      location.href.includes('downloading')
    ) {
      return
    }

    consoleOnce('looking for camera position', {
      pos: raycaster.camera.position,
      rot: raycaster.camera.rotation,
    })

    editCameraBoundingBox(raycaster.camera.position, raycaster.camera.rotation)

    // OBB wall/ceiling culling: throttle + only write store when visibility actually changes
    wallOcclusionFrameRef.current += 1
    if (wallOcclusionFrameRef.current % 3 === 0) {
      for (const [id, itemOBB] of sceneBoundingBoxes.entries()) {
        const _id = id.replace('COBB_', '') as AnyID
        const wallInfo = items.get(_id)
        if (!wallInfo) continue
        const intersects = cameraOBB.intersectsOBB(itemOBB.boundingBox)
        const nextWallVisible = !intersects
        const prevWallVisible = wallVisibleCacheRef.current.get(_id)

        if (prevWallVisible !== nextWallVisible) {
          wallVisibleCacheRef.current.set(_id, nextWallVisible)
          startTransition(() => {
            editWall(_id, { wallVisible: nextWallVisible })
          })
        }

        if (!wallInfo.visible && !wallYResetDoneRef.current.has(_id)) {
          wallYResetDoneRef.current.add(_id)
          startTransition(() => {
            editWall(_id, { y: 0 })
          })
        } else if (wallInfo.visible) {
          wallYResetDoneRef.current.delete(_id)
        }
      }

      if (VERTECHO) {
        for (const [id, itemOBB] of sceneBoundingBoxes.entries()) {
          if (!id.startsWith('COBB_CEILING_')) continue
          const areaId = id.replace('COBB_CEILING_', '')
          const areaInfo = areas.get(areaId)
          if (!areaInfo || !isArea(areaInfo) || !areaInfo.isRoof) continue

          const intersects = cameraOBB.intersectsOBB(itemOBB.boundingBox)
          const nextCeilingVisible = !intersects
          const prevCeilingVisible = ceilingVisibleCacheRef.current.get(areaId)

          if (prevCeilingVisible !== nextCeilingVisible) {
            ceilingVisibleCacheRef.current.set(areaId, nextCeilingVisible)
            startTransition(() => {
              editAreaProperty(areaId, { ceilingVisible: nextCeilingVisible })
            })
          }
        }
      }
    }

    if (
      cameraControlsEnabled &&
      controlsRef.current &&
      cameraObjectRef.current &&
      navigationMode === 'user'
    ) {
      clampCameraElevationViaControls(controlsRef.current, cameraObjectRef.current)
    }
  })
  return (
    <group>
      <PerspectiveCamera
        makeDefault
        frustumCulled={false}
        ref={cameraObjectRef}
        up={[0, 1500, 0]}
        near={CAMERA_3D_NEAR}
        far={CAMERA_3D_FAR}
        fov={CAMERA_3D_FOV}
      />
      <CameraControls
        makeDefault
        ref={controlsRef}
        infinityDolly={true}
        smoothTime={CONTROLS_3D_SMOOTH}
        dollySpeed={CONTROLS_DOLLY_SPEED}
        minDistance={CONTROLS_3D_MIN_DISTANCE}
        enabled={cameraControlsEnabled}
      />
    </group>
  )
}
